// Centralized number / money formatters. Pages should not roll their own
// Number(x).toFixed(...) chains — they end up inconsistent.

export type Numeric = string | number | null | undefined;

function toNumber(v: Numeric): number {
  if (v === null || v === undefined) return NaN;
  if (typeof v === "number") return v;
  const n = Number(v);
  return n;
}

// 1,23,456.78 — Indian grouping; suitable for rupee amounts.
const inrFmt = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const inrFmt0 = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 0,
});

const compactFmt = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 2,
});

const pctFmt = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const intFmt = new Intl.NumberFormat("en-IN");

export function formatINR(v: Numeric, opts?: { decimals?: 0 | 2; sign?: boolean }): string {
  const n = toNumber(v);
  if (!Number.isFinite(n)) return "—";
  const f = (opts?.decimals ?? 2) === 0 ? inrFmt0 : inrFmt;
  const abs = Math.abs(n);
  const prefix = opts?.sign && n > 0 ? "+₹" : n < 0 ? "−₹" : "₹";
  return `${prefix}${f.format(abs)}`;
}

export function formatCompact(v: Numeric): string {
  const n = toNumber(v);
  if (!Number.isFinite(n)) return "—";
  return compactFmt.format(n);
}

export function formatPct(v: Numeric, opts?: { sign?: boolean }): string {
  const n = toNumber(v);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const prefix = opts?.sign && n > 0 ? "+" : n < 0 ? "−" : "";
  return `${prefix}${pctFmt.format(abs)}%`;
}

export function formatInt(v: Numeric): string {
  const n = toNumber(v);
  if (!Number.isFinite(n)) return "—";
  return intFmt.format(n);
}

// Tailwind class chooser for signed numerics. Returns text-up / text-down /
// text-fg-subtle depending on sign; safe across light + dark themes.
export function signedClass(v: Numeric): string {
  const n = toNumber(v);
  if (!Number.isFinite(n) || n === 0) return "text-fg-subtle";
  return n > 0 ? "text-up-600 dark:text-up-400" : "text-down-600 dark:text-down-400";
}

// Render UTC-naive ISO timestamps as IST. Backend stores datetime without tz,
// and JS Date parses bare ISO strings as local — so we append Z first.
export function formatIst(iso: string, opts?: { withSeconds?: boolean }): string {
  const date = new Date(iso + (iso.endsWith("Z") ? "" : "Z"));
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: opts?.withSeconds ? "2-digit" : undefined,
    hour12: false,
  });
}

// Time-only IST helper for activity feeds.
export function formatIstTime(iso: string): string {
  const date = new Date(iso + (iso.endsWith("Z") ? "" : "Z"));
  return date.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

// Relative "5m ago" / "2h ago" / "3d ago". Returns "just now" for <1m.
export function formatRelative(iso: string): string {
  const date = new Date(iso + (iso.endsWith("Z") ? "" : "Z"));
  const diff = Date.now() - date.getTime();
  const sec = Math.max(0, Math.floor(diff / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatIst(iso).split(",")[0]!;
}
